// ROOM 404: "Add to Home Screen" PWA Guidance Modal
// Provides mobile-first installation walkthrough without repeatedly nagging users.

import React from 'react';
import {
  Download,
  ExternalLink,
  PlusSquare,
  Share,
  Smartphone,
  X,
} from 'lucide-react';
import { dismissInstallPrompt, getDeviceAndPushSupport } from '../services/push';

interface InstallGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  deferredPrompt?: any;
}

export const InstallGuideModal: React.FC<InstallGuideModalProps> = ({
  isOpen,
  onClose,
  deferredPrompt,
}) => {
  if (!isOpen) return null;

  const { isIos } = getDeviceAndPushSupport();

  const handleDismissForever = () => {
    dismissInstallPrompt();
    onClose();
  };

  const handleNativeInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        dismissInstallPrompt();
      }
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="w-full max-w-md bg-[#12141c] border border-[#1f2433] rounded-3xl p-6 shadow-2xl space-y-5 text-xs text-gray-200 relative">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#1f2433] pb-3">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
              <Smartphone className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">Install ROOM 404</h3>
              <p className="text-[11px] text-gray-400 font-mono">Mobile App Experience & Push Alerts</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-[#1f2433] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Device-Specific Steps */}
        {isIos ? (
          <div className="space-y-4">
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-amber-300 text-[11px] leading-relaxed">
              <strong>iOS Requirement:</strong> Apple requires adding ROOM 404 to your Home Screen in Safari before Web Push notifications can be activated.
            </div>

            <ol className="space-y-3 font-sans">
              <li className="flex items-start space-x-3 p-3 rounded-2xl bg-[#090a0f] border border-[#1f2433]">
                <div className="w-6 h-6 rounded-lg bg-[#1f2433] text-white font-bold flex items-center justify-center text-xs flex-shrink-0">
                  1
                </div>
                <div className="space-y-1">
                  <div className="font-bold text-white flex items-center space-x-1.5">
                    <span>Tap the Safari Share Button</span>
                    <Share className="w-3.5 h-3.5 text-cyan-400 inline" />
                  </div>
                  <p className="text-gray-400 text-[11px]">
                    Located in the toolbar at the bottom of Safari on iPhone, or top bar on iPad.
                  </p>
                </div>
              </li>

              <li className="flex items-start space-x-3 p-3 rounded-2xl bg-[#090a0f] border border-[#1f2433]">
                <div className="w-6 h-6 rounded-lg bg-[#1f2433] text-white font-bold flex items-center justify-center text-xs flex-shrink-0">
                  2
                </div>
                <div className="space-y-1">
                  <div className="font-bold text-white flex items-center space-x-1.5">
                    <span>Select &ldquo;Add to Home Screen&rdquo;</span>
                    <PlusSquare className="w-3.5 h-3.5 text-emerald-400 inline" />
                  </div>
                  <p className="text-gray-400 text-[11px]">
                    Scroll down the action sheet and select &ldquo;Add to Home Screen&rdquo;.
                  </p>
                </div>
              </li>

              <li className="flex items-start space-x-3 p-3 rounded-2xl bg-[#090a0f] border border-[#1f2433]">
                <div className="w-6 h-6 rounded-lg bg-[#1f2433] text-white font-bold flex items-center justify-center text-xs flex-shrink-0">
                  3
                </div>
                <div className="space-y-1">
                  <div className="font-bold text-white">Open from Home Screen</div>
                  <p className="text-gray-400 text-[11px]">
                    Launch ROOM 404 from your home screen icon for full-screen view and instant emergency alerts.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-300 leading-relaxed">
              Install ROOM 404 directly to your device for rapid home-screen access, full-screen live chat, and instant push bulletins.
            </p>

            {deferredPrompt ? (
              <button
                onClick={handleNativeInstall}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-black font-bold rounded-2xl text-xs flex items-center justify-center space-x-2 shadow-lg shadow-emerald-500/10 active:scale-95 transition-all"
              >
                <Download className="w-4 h-4" />
                <span>Install Application Now</span>
              </button>
            ) : (
              <div className="p-3.5 rounded-2xl bg-[#090a0f] border border-[#1f2433] space-y-2">
                <div className="font-bold text-white flex items-center space-x-1.5">
                  <ExternalLink className="w-3.5 h-3.5 text-cyan-400" />
                  <span>Browser Menu Installation</span>
                </div>
                <p className="text-gray-400 text-[11px] leading-relaxed">
                  Open your browser menu (⋮ or ⋯) and select <strong>&ldquo;Install App&rdquo;</strong> or <strong>&ldquo;Add to Home screen&rdquo;</strong>.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-[#1f2433]">
          <button
            onClick={handleDismissForever}
            className="text-[11px] text-gray-500 hover:text-gray-300 transition-colors"
          >
            Don&apos;t ask again for 14 days
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#1f2433] hover:bg-[#282f42] text-white font-semibold rounded-xl text-xs transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};
